import React, { createContext, useContext, useState, useEffect } from 'react';
import { permissionService } from '../services/permission.service';
import { useAuth } from './AuthContext';

// Create Permission Context
const PermissionContext = createContext();

/**
 * Permission Provider Component
 * Manages user permissions globally and provides permission checking utilities
 */
export function PermissionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserPermissions();
    } else {
      setPermissions([]);
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const fetchUserPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.getMyPermissions();
      setPermissions(response.data || []);
    } catch (err) {
      console.error('❌ [PERMISSION CONTEXT] Error fetching permissions:', err);
      setError(err.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (resource, action) => {
    // Admin has all permissions
    if (user?.role === 'admin') {
      return true;
    }
    return permissionService.hasPermission(permissions, resource, action);
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionChecks) => {
    // Admin has all permissions
    if (user?.role === 'admin') {
      return true;
    }
    return permissionService.hasAnyPermission(permissions, permissionChecks);
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionChecks) => {
    // Admin has all permissions
    if (user?.role === 'admin') {
      return true;
    }
    return permissionService.hasAllPermissions(permissions, permissionChecks);
  };

  /**
   * Get permissions grouped by resource
   */
  const getGroupedPermissions = () => {
    return permissionService.groupByResource(permissions);
  };

  /**
   * Refresh permissions (useful after role changes)
   */
  const refreshPermissions = async () => {
    await fetchUserPermissions();
  };

  const value = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getGroupedPermissions,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Custom hook to use Permission Context
 */
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

export default PermissionContext;
